import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Bot, User, Loader2, ThumbsUp, ThumbsDown, MessageSquare, History } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  feedback?: boolean | null;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface LiveChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMessage?: string;
}

const LiveChatModal: React.FC<LiveChatModalProps> = ({ open, onOpenChange, initialMessage }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: "Hi! I'm Voy, your OdyVoy support assistant. How can I help you today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const { toast } = useToast();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasProcessedInitialMessage = useRef(false);

  const quickReplies = [
    { text: "How do I upgrade my plan?", icon: "💳" },
    { text: "How do packing lists work?", icon: "🎒" },
    { text: "What are the pricing plans?", icon: "💰" },
    { text: "How do I use currency converter?", icon: "💱" },
    { text: "How do I cancel my subscription?", icon: "❌" },
  ];

  const followUpReplies = [
    { text: "Yes", icon: "✅" },
    { text: "No", icon: "❌" },
  ];

  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Load conversations when modal opens
  useEffect(() => {
    if (open && user) {
      loadConversations();
      // Create or load current conversation
      if (!currentConversationId) {
        createNewConversation();
      }
    }
  }, [open, user]);

  // Handle initial message when modal opens
  useEffect(() => {
    if (open && initialMessage && !hasProcessedInitialMessage.current && currentConversationId) {
      hasProcessedInitialMessage.current = true;
      setInput(initialMessage);
      // Auto-send after a brief delay to ensure UI is ready
      setTimeout(() => {
        sendMessage(initialMessage);
      }, 300);
    }
    
    // Reset when modal closes
    if (!open) {
      hasProcessedInitialMessage.current = false;
    }
  }, [open, initialMessage, currentConversationId]);

  const loadConversations = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('chat_conversations')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error loading conversations:', error);
      return;
    }

    setConversations(data || []);
  };

  const createNewConversation = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('chat_conversations')
      .insert({
        user_id: user.id,
        title: 'New Conversation'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      return;
    }

    setCurrentConversationId(data.id);
  };

  const loadConversation = async (conversationId: string) => {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    if (data && data.length > 0) {
      const loadedMessages = data.map(msg => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: msg.created_at
      }));
      setMessages(loadedMessages);
    } else {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm Voy, your OdyVoy support assistant. How can I help you today?",
        timestamp: new Date().toISOString()
      }]);
    }

    setCurrentConversationId(conversationId);
    setShowHistory(false);
  };

  const saveMessage = async (message: Omit<Message, 'id'>) => {
    if (!user || !currentConversationId) return null;

    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: currentConversationId,
        role: message.role,
        content: message.content
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving message:', error);
      return null;
    }

    // Update conversation's updated_at
    await supabase
      .from('chat_conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', currentConversationId);

    return data.id;
  };

  const handleFeedback = async (messageId: string | undefined, isHelpful: boolean) => {
    if (!messageId || !user) return;

    const { error } = await supabase
      .from('chat_feedback')
      .upsert({
        message_id: messageId,
        user_id: user.id,
        is_helpful: isHelpful
      });

    if (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: "Error",
        description: "Failed to save feedback",
        variant: "destructive",
      });
      return;
    }

    // Update local message state
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, feedback: isHelpful } : msg
    ));

    toast({
      title: "Thank you!",
      description: "Your feedback helps us improve",
    });
  };

  // Detect user interest in subscriptions
  const detectInterest = async (userMessage: string, aiResponse: string) => {
    if (!user) return;

    const messageLower = userMessage.toLowerCase();
    const responseLower = aiResponse.toLowerCase();
    
    // Check if message shows interest and AI mentioned pricing/upgrade
    const interestKeywords = [
      'interested in', 'want to', 'how do i upgrade', 'how to upgrade',
      'ready to', 'i\'d like', 'tell me more about', 'how much',
      'what\'s the price', 'pricing for', 'cost of', 'subscribe'
    ];
    
    const subscriptionKeywords = [
      { type: 'premium_economy', keywords: ['premium economy', 'premium class'] },
      { type: 'first_class', keywords: ['first class', 'ultimate'] },
      { type: 'boost_pass', keywords: ['boost pass', '24 hour', '24-hour'] },
      { type: 'gift_subscription', keywords: ['gift', 'gift subscription'] }
    ];

    const showsInterest = interestKeywords.some(keyword => messageLower.includes(keyword));
    const mentionsPricing = responseLower.includes('pricing') || 
                           responseLower.includes('upgrade') ||
                           responseLower.includes('€') ||
                           responseLower.includes('odyvoy.com/pricing');

    if (showsInterest || mentionsPricing) {
      // Determine which subscription type they're interested in
      for (const sub of subscriptionKeywords) {
        const isInterested = sub.keywords.some(keyword => 
          messageLower.includes(keyword) || responseLower.includes(keyword)
        );

        if (isInterested) {
          try {
            await supabase.from('conversion_opportunities').insert({
              user_id: user.id,
              email: user.email,
              interest_type: sub.type,
              conversation_id: currentConversationId,
              expressed_at: new Date().toISOString()
            });
            console.log(`Logged conversion opportunity: ${sub.type}`);
          } catch (error) {
            console.error('Error logging conversion opportunity:', error);
          }
          break; // Only log one type per message
        }
      }
    }
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || isLoading) return;

    const userMessage = {
      role: 'user' as const,
      content: textToSend,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Save user message
    const userMessageId = await saveMessage(userMessage);

    try {
      const conversation = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          message: textToSend,
          conversation
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error('Failed to get response from support agent');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const aiMessage: Message = {
        role: 'assistant',
        content: data.response,
        timestamp: data.timestamp || new Date().toISOString()
      };

      // Save AI message
      const aiMessageId = await saveMessage(aiMessage);
      
      setMessages(prev => [...prev, { ...aiMessage, id: aiMessageId || undefined }]);

      // Detect if user expressed interest in subscriptions
      await detectInterest(textToSend, data.response);

      // Add follow-up message after a brief delay
      setTimeout(async () => {
        const followUpMessage: Message = {
          role: 'assistant',
          content: "Is there anything else I can assist you with?",
          timestamp: new Date().toISOString()
        };
        const followUpMessageId = await saveMessage(followUpMessage);
        setMessages(prev => [...prev, { ...followUpMessage, id: followUpMessageId || undefined }]);
        setShowFollowUp(true);
      }, 500);
    } catch (error) {
      console.error('Error sending message:', error);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message. Please try again.",
        variant: "destructive",
      });
      
      const errorMessage: Message = {
        role: 'assistant',
        content: error instanceof Error && error.message ? `I'm sorry, I encountered an error: ${error.message}` : "I'm sorry, I'm having trouble responding right now. Please try again or contact human support at info@odyvoy.com if the issue persists.",
        timestamp: new Date().toISOString()
      };
      const errorMessageId = await saveMessage(errorMessage);
      setMessages(prev => [...prev, { ...errorMessage, id: errorMessageId || undefined }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollowUpResponse = async (response: string) => {
    setShowFollowUp(false);
    
    if (response === "Yes") {
      // Reset to show initial quick replies
      const resetMessage: Message = {
        role: 'assistant',
        content: "Great! What else would you like to know?",
        timestamp: new Date().toISOString()
      };
      const resetMessageId = await saveMessage(resetMessage);
      setMessages(prev => [...prev, { ...resetMessage, id: resetMessageId || undefined }]);
    } else {
      // User said "No"
      const closingMessage: Message = {
        role: 'assistant',
        content: "Thank you for chatting with me! Feel free to reach out anytime you need help. Have a great day! 😊",
        timestamp: new Date().toISOString()
      };
      const closingMessageId = await saveMessage(closingMessage);
      setMessages(prev => [...prev, { ...closingMessage, id: closingMessageId || undefined }]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md h-[600px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              OdyVoy Support Hub
            </div>
            {user && conversations.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
              >
                <History className="h-4 w-4" />
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {showHistory ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="p-4 border-b">
              <h3 className="font-semibold mb-2">Conversation History</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  createNewConversation();
                  setMessages([{
                    role: 'assistant',
                    content: "Hi! I'm Voy, your OdyVoy support assistant. How can I help you today?",
                    timestamp: new Date().toISOString()
                  }]);
                  setShowHistory(false);
                }}
                className="w-full"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Start New Conversation
              </Button>
            </div>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <Button
                    key={conv.id}
                    variant={conv.id === currentConversationId ? "secondary" : "ghost"}
                    className="w-full justify-start text-left"
                    onClick={() => loadConversation(conv.id)}
                  >
                    <div className="flex flex-col items-start w-full">
                      <span className="font-medium truncate w-full">
                        {conv.title || 'Untitled Conversation'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <>
            <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`flex items-start gap-2 max-w-[80%] ${
                        message.role === 'user' ? 'flex-row-reverse' : 'flex-col'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          {message.role === 'user' ? (
                            <User className="h-4 w-4" />
                          ) : (
                            <Bot className="h-4 w-4" />
                          )}
                        </div>
                        <div
                          className={`p-3 rounded-lg ${
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                      </div>
                      
                      {message.role === 'assistant' && message.id && (
                        <div className="flex gap-1 ml-10">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFeedback(message.id, true)}
                            disabled={message.feedback !== undefined}
                            className={`h-7 w-7 p-0 ${message.feedback === true ? 'text-green-600' : ''}`}
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleFeedback(message.id, false)}
                            disabled={message.feedback !== undefined}
                            className={`h-7 w-7 p-0 ${message.feedback === false ? 'text-red-600' : ''}`}
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {((messages.length === 1 && messages[0].role === 'assistant') || 
                  (messages.length >= 2 && messages[messages.length - 1].content === "Great! What else would you like to know?")) && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-3">Quick questions:</p>
                    <div className="flex flex-wrap gap-2">
                      {quickReplies.map((reply, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowFollowUp(false);
                            sendMessage(reply.text);
                          }}
                          disabled={isLoading}
                          className="text-xs"
                        >
                          <span className="mr-1">{reply.icon}</span>
                          {reply.text}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {showFollowUp && !isLoading && (
                  <div className="mt-4">
                    <div className="flex gap-2">
                      {followUpReplies.map((reply, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => handleFollowUpResponse(reply.text)}
                          className="text-xs"
                        >
                          <span className="mr-1">{reply.icon}</span>
                          {reply.text}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2">
                      <div className="w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center">
                        <Bot className="h-4 w-4" />
                      </div>
                      <div className="bg-secondary text-secondary-foreground p-3 rounded-lg">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            <div className="flex gap-2 p-4 border-t">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button onClick={() => sendMessage()} disabled={isLoading || !input.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LiveChatModal;